/**
 * General Observations — Configuration
 *
 * Defines the panels available in the "General Observations" section of the
 * CYP ASD Behavioural Observation module. These panels sit outside the 10
 * structured tasks and allow the clinician to capture broader behavioural
 * patterns observed during the session.
 *
 * Currently includes:
 *   • Self-reported — CAT-Q (Camouflaging Autistic Traits Questionnaire)
 *     statements that the clinician can discuss with the young person and
 *     record qualitative notes against.
 *   • Sensory (B4) — placeholder for a future sensory observations panel.
 */

// ─────────────────────────────────────────────────────────────────────────
//  CAT-Q Statements — grouped by the three subscales
//
//  The CAT-Q is a 25-item self-report measure of social camouflaging
//  behaviours. Items marked (R) are reverse-scored in the original
//  instrument; we display the statement text as-is and ignore scoring
//  direction since we are using them as discussion prompts, not scoring.
// ─────────────────────────────────────────────────────────────────────────

export const CATQ_SUBSCALES = [
  {
    id: 'compensation',
    title: 'Compensation',
    description: 'Strategies used to actively compensate for social-communication difficulties.',
    statements: [
      { number: 1,  text: 'When I am interacting with someone, I deliberately copy their body language or facial expressions.' },
      { number: 4,  text: 'I have developed a script to follow in social situations.' },
      { number: 5,  text: 'I will repeat phrases that I have heard others say in the exact same way that I first heard them.' },
      { number: 8,  text: 'In my own social interactions, I use behaviours that I have learned from watching other people interacting.' },
      { number: 14, text: 'I have tried to improve my understanding of social skills by watching other people.' },
      { number: 17, text: 'I have researched the rules of social interactions to improve my own social skills.' },
      { number: 20, text: 'I learn how people use their bodies and faces to interact by watching television or films, or by reading fiction.' },
      { number: 23, text: 'I have spent time learning social skills from television shows and films, and try to use these in my interactions.' },
    ],
  },
  {
    id: 'masking',
    title: 'Masking',
    description: 'Behaviours aimed at hiding autistic characteristics to blend in socially.',
    statements: [
      { number: 2,  text: 'I monitor my body language or facial expressions so that I appear relaxed.' },
      { number: 6,  text: 'I adjust my body language or facial expressions so that I appear interested by the person I am interacting with.' },
      { number: 9,  text: 'I always think about the impression I make on other people.' },
      { number: 11, text: 'I practice my facial expressions and body language to make sure they look natural.' },
      { number: 15, text: 'I monitor my body language or facial expressions so that I appear interested by the person I am interacting with.' },
      { number: 18, text: 'I am always aware of the impression I make on other people.' },
      { number: 21, text: 'I adjust my body language or facial expressions so that I appear relaxed.' },
      { number: 25, text: 'In social situations, I feel like I am pretending to be \'normal\'.' },
    ],
  },
  {
    id: 'assimilation',
    title: 'Assimilation',
    description: 'The degree to which a person fits in or feels the need to manage social situations.',
    statements: [
      { number: 3,  text: 'I rarely feel the need to put on an act in order to get through a social situation.', reversed: true },
      { number: 7,  text: 'In social situations, I feel like I\'m \'performing\' rather than being myself.' },
      { number: 10, text: 'I need the support of other people in order to socialise.' },
      { number: 12, text: 'I don\'t feel the need to make eye contact with other people if I don\'t want to.', reversed: true },
      { number: 13, text: 'I have to force myself to interact with people when I am in social situations.' },
      { number: 16, text: 'When in social situations, I try to find ways to avoid interacting with others.' },
      { number: 19, text: 'I feel free to be myself when I am with other people.', reversed: true },
      { number: 22, text: 'When talking to other people, I feel like the conversation flows naturally.', reversed: true },
      { number: 24, text: 'In social interactions, I do not pay attention to what my face or body are doing.', reversed: true },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
//  General Questions — alternative to CAT-Q
//
//  Some clinicians prefer a broader set of questions that probe restricted/
//  repetitive behaviours, rigidity, and rule-following rather than the
//  camouflaging-focused CAT-Q. This flat list can be asked in any order.
// ─────────────────────────────────────────────────────────────────────────

export const GENERAL_QUESTIONS = [
  'Do you have a favoured word?',
  'Do you like to collect anything?',
  'Do you like to have things in a certain way in your room?',
  'Do you like to watch the same things over and over?',
  'How do you feel when things change?',
  'Do you do things in the same order every day? If interrupted, what happens?',
  'Do you follow the rules?',
  'Do things have to be perfect?',
  'If someone is doing something wrong, what do you do?',
];

// ─────────────────────────────────────────────────────────────────────────
//  Sensory (B4) probing questions — grouped by assessment domain
//
//  These cover the full sensory profile relevant to DSM-5 Criterion B4
//  (Hyper- or hyporeactivity to sensory input or unusual sensory interests).
//  The clinician uses these as discussion prompts with the CYP/parent and
//  records qualitative notes + an overall B4 evidence decision.
// ─────────────────────────────────────────────────────────────────────────

export const SENSORY_CATEGORIES = [
  {
    id: 'general',
    title: 'General Sensory Profile',
    questions: [
      'Are there any sensory sensitivities noticed (e.g. sound, light, touch, smell, taste, movement)?',
      'Are there any sensations they seek out or particularly enjoy?',
      'Have these sensory differences been present since early childhood, or did they emerge later?',
      'Do sensory difficulties change with stress, tiredness, or anxiety?',
    ],
  },
  {
    id: 'auditory',
    title: 'Sound / Auditory Sensitivity',
    questions: [
      'How do they respond to loud or sudden noises (e.g. alarms, hand dryers, fireworks)?',
      'Do background noises (e.g. chatter, traffic, humming) affect concentration?',
      'Do they cover their ears, leave the room, or become distressed?',
      'Do they use coping strategies (e.g. ear defenders, headphones, avoiding certain places)?',
    ],
  },
  {
    id: 'visual',
    title: 'Visual Sensitivity',
    questions: [
      'How do they cope with bright lights, fluorescent lighting, flickering, or busy environments?',
      'Do visual environments (e.g. shops, classrooms, screens) feel overwhelming?',
      'Do they prefer dim lighting or specific visual setups?',
    ],
  },
  {
    id: 'tactile',
    title: 'Touch / Tactile Sensitivity',
    questions: [
      'Are they sensitive to clothing textures, labels, seams, socks, or certain fabrics?',
      'How do they feel about unexpected touch or physical contact?',
      'Are there difficulties with temperature, hair brushing, teeth brushing, or nail cutting?',
    ],
  },
  {
    id: 'taste_smell',
    title: 'Taste, Smell, and Food Sensitivities',
    questions: [
      'Are there strong preferences or aversions related to food textures, tastes, or temperatures?',
      'Are smells (e.g. perfume, cleaning products, food) overwhelming or distressing?',
      'Is their diet limited due to sensory reasons rather than appetite?',
    ],
  },
  {
    id: 'movement',
    title: 'Movement, Balance, and Body Awareness',
    questions: [
      'How do they respond to swings, rides, heights, or balance activities?',
      'Do they avoid or seek movement (e.g. rocking, pacing, spinning)?',
      'Do they appear clumsy, crash into things, or struggle with coordination?',
    ],
  },
  {
    id: 'interoception',
    title: 'Interoception (Internal Sensations)',
    questions: [
      'Do they notice when they are hungry, thirsty, tired, hot/cold, or need the toilet?',
      'Do they recognise early signs of stress or overwhelm?',
      'Do physical sensations build up suddenly rather than gradually?',
    ],
  },
  {
    id: 'daily_impact',
    title: 'Impact on Daily Life',
    questions: [
      'How do sensory differences affect school, work, social situations, or routines?',
      'Do sensory issues contribute to meltdowns, shutdowns, avoidance, or anxiety?',
      'What adjustments or strategies help (e.g. routine, preparation, sensory tools)?',
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
//  General Observation Panels
// ─────────────────────────────────────────────────────────────────────────

export const GENERAL_PANELS = [
  {
    id: 'self_reported',
    title: 'Self-reported',
    description: 'CAT-Q — Camouflaging Autistic Traits Questionnaire',
    available: true,
  },
  {
    id: 'sensory',
    title: 'Sensory (B4)',
    description: 'Sensory reactivity observations',
    available: true,
  },
];
